// quotation-preview Footer.js
import React from "react";
import signatures from "../../utils/signatures";
import { View, Text, Image } from "@react-pdf/renderer";
import { styles } from "../styles";

const formatNumber = (num) => {
  return parseFloat(num || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const Footer = ({ quotationData, bankInfo }) => {
  const MAX_REMARK_LINES = 5;

  // 🟦 MAP รายชื่อผู้มีสิทธิ์เซ็นของแต่ละบริษัท
  const SIGNERS = {
    OPTX: {
      proposedBy: {
        email: "chawachai@optx.co.th",
        name: "CHAWACHAI WICHAIDIT",
      },
      approvedBy: {
        email: "chawachai@optx.co.th",
        name: "CHAWACHAI WICHAIDIT",
      },
    },
    NEON: {
      proposedBy: {
        email: "natorn@neonworks.co.th",
        name: "NATORN MANEEKORN",
      },
      approvedBy: {
        email: "akedanai@neonworks.co.th",
        name: "AKEDANAI YUKTANAN",
      },
    },
  };

  // Public e-sign pages may not have the same logged-in company as the document.
  // Prefer the document creator email so Proposed/Approved signatures match the QT owner.
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const rawCompany = String(
    quotationData.createdByUser || quotationData.company || user.company || ""
  )
    .trim()
    .toUpperCase();

  // ถ้ามีคำว่า NEON → ใช้เซ็ต NEON, นอกนั้น default เป็น OPTX
  const companyKey = rawCompany.includes("NEON") || rawCompany.includes("@NEON")
    ? "NEON"
    : "OPTX";
  const signers = SIGNERS[companyKey];

  // 🟦 หาอีเมลที่ Approved ใน flow เพื่อแสดงลายเซ็น
  const approvalSteps =
    quotationData?.approvalHierarchy?.[0]?.approvalHierarchy ||
    quotationData?.approvalHierarchy ||
    [];
  const approvedEmailsFromFlow = Array.isArray(approvalSteps)
    ? approvalSteps
        .filter((entry) => entry?.status === "Approved" && entry?.approver)
        .map((entry) => entry.approver.toLowerCase())
    : [];
  const isDocumentApproved =
    quotationData?.approvalStatus === "Approved" ||
    quotationData?.status === "Approved";
  const fallbackApprovedEmails = isDocumentApproved
    ? [signers.proposedBy.email, signers.approvedBy.email]
    : [];
  const approvedEmails = Array.from(
    new Set([...approvedEmailsFromFlow, ...fallbackApprovedEmails])
  );

  const renderRemarkSection = () => {
    const remarkText = quotationData.remark
      ? quotationData.remark.split("\n").slice(0, MAX_REMARK_LINES).join("\n")
      : "-";

    return (
      <View style={styles.remarkContainer}>
        <Text style={styles.remarkTitle}>Remark</Text>
        <Text style={styles.remarkText}>{remarkText}</Text>
      </View>
    );
  };

  const renderSummaryAndPaymentDetails = () => (
    <>
      {renderRemarkSection()} {/* ✅ แสดง Remark ก่อน Summary */}
      <View style={styles.summaryContainer}>
        {/* Payment Details + Bank Account Info */}
        <View style={styles.paymentDetails}>
          {/* Credit Term เดิม (ถ้าจะใช้ค่อย uncomment) */}
          {/* 
          <Text style={{ fontWeight: "bold", marginBottom: 5 }}>
            รายละเอียดการชำระเงิน :
          </Text>
          <Text>Payment Details</Text>
          <Text>
            - ระยะเวลา Credit Term :{" "}
            {quotationData.CreditTerm === "0"
              ? "see remark"
              : `${quotationData.CreditTerm} วัน`}
          </Text>
          <View style={styles.borderLine}></View>
          */}

          <Text style={{ fontWeight: "bold", marginTop: 5 }}>
            You can transfer to owner account:
          </Text>
          <Text>
            <Text style={{ fontWeight: "bold" }}>ACCOUNT OWNER : </Text>
            {bankInfo.accountOwner || "-"}
          </Text>
          <Text>
            <Text style={{ fontWeight: "bold" }}>ACCOUNT NO : </Text>
            {bankInfo.accountNo || "-"}
          </Text>
          <Text>
            <Text style={{ fontWeight: "bold" }}>ACCOUNT TYPE : </Text>
            {bankInfo.accountType || "-"}
          </Text>
          <Text>
            <Text style={{ fontWeight: "bold" }}>BANK NAME : </Text>
            {bankInfo.bankName || "-"}
          </Text>
          <Text>
            <Text style={{ fontWeight: "bold" }}>BRANCH NAME : </Text>
            {bankInfo.branchName || "-"}
          </Text>
          <Text>
            <Text style={{ fontWeight: "bold" }}>BANK ADDRESS : </Text>
            {bankInfo.bankAddress || "-"}
          </Text>
          <Text>
            <Text style={{ fontWeight: "bold" }}>SWIFT CODE : </Text>
            {bankInfo.swiftCode || "-"}
          </Text>
        </View>

        {/* ✅ ฝั่งขวา (Summary Fields) */}
        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text>Total:</Text>
            <Text>{formatNumber(quotationData.totalBeforeFee)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Fee ({quotationData.fee}%):</Text>
            <Text>
              {formatNumber(
                 quotationData.totalBeforeFee * (quotationData.fee / 100)
              )}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Grand Total:</Text>
            <Text>{formatNumber(quotationData.total)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Discount:</Text>
            <Text>{formatNumber(quotationData.discount)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Amount Before VAT:</Text>
            <Text>{formatNumber(quotationData.amountBeforeTax)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>VAT (7%):</Text>
            <Text>{formatNumber(quotationData.vat)}</Text>
          </View>
          <View style={styles.summaryRowLast}>
            <Text>Net Amount:</Text>
            <Text>{formatNumber(quotationData.netAmount)}</Text>
          </View>
        </View>
      </View>
      <View style={styles.textCondition}>
        <Text>
          (1) The quoted price is the price of wages in accordance with the
          scope of the job description set above or as attached.
        </Text>
        <Text>
          (2) If the customer has a reason to suspend the operation. The
          employer agrees to be liable by paying the contractor for the full
          amount of the quotation.
        </Text>
        <Text>
          (3) In the event that the customer cancels the work less than 15 days
          after signing the document prior to the actual production, the company
          will charge a cancellation fee of 50% of the value of the quotation
          that has been approved.
        </Text>
      </View>
    </>
  );

  const getSignatureBlock = (roleLabel, name, email, signatureImageUrl = "") => (
    <View style={styles.signatureBlock}>
      {/* เส้นลายเซ็น */}
      <View style={{ position: "relative", height: 50, marginBottom: 4 }}>
        <View
          style={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            height: 1,
            backgroundColor: "#000", // เส้นดำ 1px
          }}
        />
        {/* ลายเซ็นซ้อนบนเส้น (เฉพาะคนที่ Approved แล้ว + มีรูปใน signatures) */}
        {(signatureImageUrl ||
          (email &&
            approvedEmails.includes(email.toLowerCase()) &&
            signatures[email])) && (
            <Image
              style={{
                position: "absolute",
                bottom: 1,
                left: "50%",
                transform: "translateX(-50%)",
                width: 80,
                height: 40,
              }}
              src={signatureImageUrl || signatures[email]}
            />
          )}
      </View>

      <Text style={{ fontWeight: "bold" }}>{roleLabel}</Text>
      <Text style={{ marginTop: 6, textAlign: "center" }}>
        ({name || "____________________________________"})
      </Text>
      <Text style={{ marginTop: 8 }}>
        วันที่/Date ............./............./.............
      </Text>
    </View>
  );

  const renderSignature = () => {
    return (
      <View style={styles.signatureSection}>
        {/* Customer */}
        {getSignatureBlock(
          "Customer",
          quotationData.clientDetails?.companyBaseName,
          "",
          quotationData.customerSignatureImageUrl
        )}
        {/* Proposed By */}
        {getSignatureBlock(
          "Proposed By",
          signers.proposedBy.name,
          signers.proposedBy.email
        )}
        {/* Approved By */}
        {getSignatureBlock(
          "Approved By",
          signers.approvedBy.name,
          signers.approvedBy.email
        )}
      </View>
    );
  };

  return (
    <View>
      {renderSummaryAndPaymentDetails()}
      {renderSignature()}
    </View>
  );
};

export default Footer;
